/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Repeat } from 'lucide-react';
import { useState } from 'react';
import { Button, Empty, Modal, Spinner } from './ui';
import { TaskForm } from './TaskForm';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDateTime } from '@/lib/format';

/** Список задач с отметкой выполнения; используется на странице задач и в карточках. */
export function TasksTable({ filter, defaults, showDone = false }: { filter: Record<string, unknown>; defaults?: Record<string, string | undefined>; showDone?: boolean }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [editing, setEditing] = useState<any | null>(null);
  const status = showDone ? 'DONE,CANCELLED' : 'TODO,IN_PROGRESS';
  const key = ['tasks', filter, status];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => api.get(`/tasks${qs({ ...filter, status, pageSize: 200 })}`) });
  const toggle = useMutation({
    mutationFn: (t: any) => api.patch(`/tasks/${t.id}`, { status: t.status === 'DONE' ? 'TODO' : 'DONE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
  const check = useMutation({
    mutationFn: ({ t, i }: { t: any; i: number }) => api.patch(`/tasks/${t.id}`, { checklist: t.checklist.map((c: any, j: number) => (j === i ? { ...c, done: !c.done } : c)) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
  const w = can('task.write');
  return (
    <div className="card">
      {w && <div className="flex justify-end border-b border-slate-200 p-2"><Button size="sm" onClick={() => setEditing({})}>Новая задача</Button></div>}
      {isLoading ? <Spinner /> : !data?.items.length ? <Empty>{showDone ? 'Выполненных задач нет' : 'Открытых задач нет'}</Empty> : (
        <ul className="divide-y divide-slate-100">
          {data.items.map((t: any) => {
            const overdue = t.dueAt && t.status !== 'DONE' && new Date(t.dueAt) < new Date();
            return (
              <li key={t.id} className="flex gap-3 px-3 py-2.5">
                <input type="checkbox" className="mt-1" checked={t.status === 'DONE'} disabled={!w} onChange={() => toggle.mutate(t)} aria-label="Выполнено" />
                <div className="min-w-0 flex-1">
                  <button className={clsx('text-left text-sm', t.status === 'DONE' && 'text-slate-400 line-through')} onClick={() => w && setEditing(t)}>
                    {t.priority === 3 && <span className="mr-1 text-red-600">!</span>}{t.title}
                  </button>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
                    {t.dueAt && <span className={clsx(overdue && 'font-medium text-red-600')}>до {fmtDateTime(t.dueAt)}</span>}
                    {t.assigneeName && <span>{t.assigneeName}</span>}
                    {t.recurrence && <span className="inline-flex items-center gap-1"><Repeat size={12} /> повторяется</span>}
                  </div>
                  {t.checklist?.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {t.checklist.map((c: any, i: number) => (
                        <li key={i} className="flex items-center gap-2 text-xs">
                          <input type="checkbox" checked={c.done} disabled={!w} onChange={() => check.mutate({ t, i })} />
                          <span className={clsx(c.done && 'text-slate-400 line-through')}>{c.text}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <Modal title={editing?.id ? 'Задача' : 'Новая задача'} open={!!editing} onClose={() => setEditing(null)}>
        {editing && <TaskForm task={editing.id ? editing : undefined} defaults={defaults} onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['tasks'] }); }} />}
      </Modal>
    </div>
  );
}
